import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import PrintSettingsCard from '../components/PrintSettingsCard';
import { getOrders, getShippingLabels, generateShippingLabel, shipOrder, shipNow, bulkShipNow, printShippingLabel, shippingLabelPdfUrl, shippingLabelZplUrl, bulkShippingLabelsUrl } from '../lib/api';
import { Truck, Printer, Package, CheckCircle, FilePdf, Barcode, FileZip, Stack, Lightning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';

export default function Shipping() {
  const { isAdmin } = useAuth();
  const { company, refresh: refreshCompany } = useCompany();
  const [packedOrders, setPackedOrders] = useState([]);
  const [shippedOrders, setShippedOrders] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carrier, setCarrier] = useState('sendle');
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkShipping, setBulkShipping] = useState(false);

  const fetchData = async () => {
    try {
      const [packedRes, shippedRes, labelsRes] = await Promise.all([
        getOrders({ status: 'packed' }),
        getOrders({ status: 'shipped' }),
        getShippingLabels()
      ]);
      setPackedOrders(packedRes.data);
      setShippedOrders(shippedRes.data);
      setLabels(labelsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerateLabel = async (orderId) => {
    try {
      await generateShippingLabel({ order_id: orderId, carrier });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleShip = async (orderId) => {
    try {
      await shipOrder(orderId);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleShipNow = async (orderId, orderNumber) => {
    try {
      const res = await shipNow(orderId, carrier);
      const tn = res?.data?.tracking_number;
      const created = res?.data?.label_created;
      if (company?.auto_print_on_ship && tn) {
        // Fire-and-forget print using the configured mode
        handlePrintLabel(tn);
      } else if (tn) {
        // Default behaviour: open the label PDF in a new tab so the operator can print
        try { window.open(shippingLabelPdfUrl(tn), '_blank', 'noopener'); } catch (_) {}
      }
      toast.success(
        created
          ? `Shipped ${orderNumber} · label generated (${tn})`
          : `Shipped ${orderNumber} · existing label reused (${tn})`
      );
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Ship Now failed';
      toast.error(msg);
    }
  };

  const getOrderLabel = (orderId) => labels.find(l => l.order_id === orderId);

  /**
   * Dispatch a label print using the tenant's configured print_mode.
   *  - browser   → open PDF in a hidden iframe and trigger window.print()
   *  - zebra     → POST raw ZPL to the local Zebra Browser Print agent
   *  - printnode → backend already submitted the job; we just toast the job id
   */
  const handlePrintLabel = async (trackingNumber) => {
    try {
      const res = await printShippingLabel(trackingNumber, {});
      const data = res?.data || {};
      const mode = data.mode;
      if (mode === 'browser') {
        // Hidden iframe → load the PDF → fire window.print() once it loads.
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = `${process.env.REACT_APP_BACKEND_URL}${data.pdf_url}`;
        iframe.onload = () => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (err) {
            // Some browsers block programmatic print on cross-origin iframes — fall back to opening in a new tab
            window.open(iframe.src, '_blank', 'noopener');
          }
          // Clean up after a short delay so the print dialog has time to take focus
          setTimeout(() => iframe.remove(), 5000);
        };
        document.body.appendChild(iframe);
        toast.success(`Print dialog opened for ${trackingNumber}`);
      } else if (mode === 'zebra') {
        // POST ZPL to the local Zebra Browser Print agent
        const agentUrl = (data.agent_url || 'http://localhost:9100').replace(/\/$/, '');
        const copies = Math.max(1, parseInt(data.copies || 1, 10));
        let zplPayload = data.zpl;
        if (copies > 1) zplPayload = Array(copies).fill(data.zpl).join('\n');
        try {
          const r = await fetch(`${agentUrl}/write`, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ device: { name: 'default' }, data: zplPayload }),
          });
          if (!r.ok) throw new Error(`Agent HTTP ${r.status}`);
          toast.success(`Sent ${copies} label${copies === 1 ? '' : 's'} to Zebra agent`);
        } catch (e) {
          toast.error(`Zebra agent not reachable at ${agentUrl}. Is Zebra Browser Print installed and running?`);
        }
      } else if (mode === 'printnode') {
        toast.success(`PrintNode job submitted (id ${data.printnode_job_id})`);
      } else {
        toast.error(`Unknown print mode: ${mode}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Print failed');
    }
  };

  const toggleBulk = (id) => {
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkShipNow = async () => {
    if (bulkSelected.length === 0) return;
    if (!window.confirm(`Ship ${bulkSelected.length} order${bulkSelected.length === 1 ? '' : 's'} now?\nLabels will be generated for any without one, orders will be marked shipped, and a ZIP of labels will download.`)) return;
    setBulkShipping(true);
    const idsToShip = [...bulkSelected];
    try {
      const res = await bulkShipNow(idsToShip, carrier);
      const { shipped = 0, skipped = 0, errors = 0, results = [] } = res?.data || {};
      // Auto-download a ZIP of labels for every order that has one
      // (newly shipped + already-shipped). Orders that errored have no label.
      const idsForZip = results
        .filter((r) => r.status === 'shipped' || r.status === 'skipped')
        .map((r) => r.order_id);
      let zipDownloaded = false;
      if (idsForZip.length > 0) {
        zipDownloaded = await downloadLabelsZip(idsForZip);
      }
      // Auto-print on ship — fire the dispatcher per tracking number when enabled.
      // Skipped orders keep their existing label so they're worth (re-)printing too.
      let autoPrinted = 0;
      if (company?.auto_print_on_ship) {
        const trackingNumbers = results
          .filter((r) => (r.status === 'shipped' || r.status === 'skipped') && r.tracking_number)
          .map((r) => r.tracking_number);
        for (const tn of trackingNumbers) {
          // eslint-disable-next-line no-await-in-loop
          await handlePrintLabel(tn);
        }
        autoPrinted = trackingNumbers.length;
      }
      const zipNote = zipDownloaded ? ' · ZIP downloaded' : '';
      const printNote = autoPrinted > 0 ? ` · ${autoPrinted} auto-printed` : '';
      if (shipped > 0) {
        toast.success(`Shipped ${shipped} order${shipped === 1 ? '' : 's'}${skipped ? ` · ${skipped} already shipped` : ''}${errors ? ` · ${errors} failed` : ''}${zipNote}${printNote}`);
      } else if (skipped > 0 && errors === 0) {
        toast.message(`No new ships — all ${skipped} were already shipped${zipNote}${printNote}.`);
      } else {
        toast.error(`Bulk ship failed (${errors} error${errors === 1 ? '' : 's'})`);
      }
      setBulkSelected([]);
      fetchData();
    } catch (e) {
      toast.error('Bulk ship failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBulkShipping(false);
    }
  };

  // Shared helper — POST /shipping/bulk-labels and stream the ZIP to the browser.
  // Returns true on success so callers can chain a confirmation toast.
  const downloadLabelsZip = async (orderIds) => {
    if (!orderIds || orderIds.length === 0) return false;
    try {
      const res = await fetch(bulkShippingLabelsUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_ids: orderIds, carrier }),
      });
      if (!res.ok) throw new Error('ZIP download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `labels-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      toast.error('ZIP download failed: ' + e.message);
      return false;
    }
  };

  const handleBulkDownload = async () => {
    if (bulkSelected.length === 0) return;
    setBulkDownloading(true);
    const ok = await downloadLabelsZip(bulkSelected);
    if (ok) {
      toast.success(`${bulkSelected.length} label${bulkSelected.length === 1 ? '' : 's'} downloaded as ZIP`);
      setBulkSelected([]);
      fetchData();
    }
    setBulkDownloading(false);
  };

  return (
    <Layout>
      <div data-testid="shipping-page" className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Shipping</h1>
          <p className="text-muted-foreground">Generate labels and ship orders</p>
        </div>

        {isAdmin && (
          <PrintSettingsCard company={company} onSaved={refreshCompany} />
        )}

        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium">Default Carrier:</span>
          <Select value={carrier} onValueChange={setCarrier}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sendle">Sendle</SelectItem>
              <SelectItem value="auspost">Australia Post</SelectItem>
            </SelectContent>
          </Select>
          {bulkSelected.length > 0 && (
            <div className="flex items-center gap-2 ml-auto px-3 py-2 bg-blue-50 border border-blue-200">
              <span className="text-sm font-bold" data-testid="bulk-selected-count">{bulkSelected.length} selected</span>
              <Button
                onClick={handleBulkShipNow}
                disabled={bulkShipping || bulkDownloading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="bulk-ship-now-btn"
                size="sm"
                title="Generate any missing labels, ship all selected orders, and auto-download a ZIP of labels"
              >
                <Lightning size={14} weight="fill" className="mr-1" />
                {bulkShipping ? 'Shipping...' : 'Bulk Ship + ZIP'}
              </Button>
              <Button
                onClick={handleBulkDownload}
                disabled={bulkDownloading || bulkShipping}
                variant="outline"
                data-testid="bulk-download-zip-btn"
                size="sm"
              >
                <FileZip size={14} className="mr-1" />
                {bulkDownloading ? 'Generating ZIP...' : 'Download ZIP'}
              </Button>
              <button
                onClick={() => setBulkSelected([])}
                className="text-xs text-muted-foreground font-bold uppercase"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <Tabs defaultValue="ready" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="ready" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              Ready to Ship ({packedOrders.length})
            </TabsTrigger>
            <TabsTrigger value="shipped" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              Shipped ({shippedOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ready" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th className="w-10">
                      <Checkbox
                        checked={bulkSelected.length > 0 && bulkSelected.length === packedOrders.length}
                        onCheckedChange={(v) => setBulkSelected(v ? packedOrders.map(o => o.id) : [])}
                        data-testid="bulk-select-all"
                      />
                    </th>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Label</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : packedOrders.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No orders ready to ship</td></tr>
                  ) : packedOrders.map(order => {
                    const label = getOrderLabel(order.id);
                    return (
                      <tr key={order.id}>
                        <td>
                          <Checkbox
                            checked={bulkSelected.includes(order.id)}
                            onCheckedChange={() => toggleBulk(order.id)}
                            data-testid={`bulk-check-${order.order_number}`}
                          />
                        </td>
                        <td className="font-mono font-semibold">{order.order_number}</td>
                        <td>{order.customer_name}</td>
                        <td className="text-sm text-muted-foreground max-w-xs truncate">{order.shipping_address}</td>
                        <td>
                          {label ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle size={16} weight="fill" className="text-green-600" />
                              <span className="font-mono text-xs">{label.tracking_number}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">No label</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            {!label && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleShipNow(order.id, order.order_number)}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`ship-now-${order.order_number}`}
                                  title="Generate label + mark shipped + open PDF"
                                >
                                  <Lightning size={14} weight="fill" className="mr-1" /> Ship Now
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleGenerateLabel(order.id)} data-testid={`gen-label-${order.order_number}`}>
                                  <Printer size={14} className="mr-1" /> Label only
                                </Button>
                              </>
                            )}
                            {label && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handlePrintLabel(label.tracking_number)}
                                  className="bg-brand hover:bg-brand/90 text-white"
                                  data-testid={`print-label-${label.tracking_number}`}
                                  title={`Print using current mode: ${company?.print_mode || 'browser'}`}
                                >
                                  <Printer size={14} className="mr-1" /> Print
                                </Button>
                                <a
                                  href={shippingLabelPdfUrl(label.tracking_number)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`pdf-label-${label.tracking_number}`}
                                >
                                  <Button size="sm" variant="outline" className="border-brand text-brand">
                                    <FilePdf size={14} className="mr-1" /> PDF
                                  </Button>
                                </a>
                                <a
                                  href={shippingLabelZplUrl(label.tracking_number)}
                                  download={`${label.tracking_number}.zpl`}
                                  data-testid={`zpl-label-${label.tracking_number}`}
                                >
                                  <Button size="sm" variant="outline" className="border-slate-400 text-foreground" title="Download Zebra ZPL II for warehouse printer">
                                    <Barcode size={14} className="mr-1" /> ZPL
                                  </Button>
                                </a>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    const url = `${window.location.origin}/track/${label.tracking_number}`;
                                    try {
                                      await navigator.clipboard.writeText(url);
                                      toast.success(`Customer tracking link copied: ${url}`);
                                    } catch (_) {
                                      window.prompt('Copy this customer tracking link:', url);
                                    }
                                  }}
                                  data-testid={`copy-track-${label.tracking_number}`}
                                  title="Copy public customer tracking link"
                                >
                                  Copy link
                                </Button>
                                <Button size="sm" onClick={() => handleShip(order.id)} className="bg-green-600 hover:bg-green-700" data-testid={`ship-${order.order_number}`}>
                                  <Truck size={14} className="mr-1" /> Ship
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="shipped" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Carrier</th>
                    <th>Tracking #</th>
                    <th>Shipped At</th>
                    <th>Label</th>
                  </tr>
                </thead>
                <tbody>
                  {shippedOrders.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No shipped orders</td></tr>
                  ) : shippedOrders.map(order => (
                    <tr key={order.id}>
                      <td className="font-mono font-semibold">{order.order_number}</td>
                      <td>{order.customer_name}</td>
                      <td><span className="text-xs uppercase font-bold text-muted-foreground">{order.carrier || '-'}</span></td>
                      <td className="font-mono text-sm">{order.tracking_number || '-'}</td>
                      <td className="text-sm text-muted-foreground">
                        {order.shipped_at ? new Date(order.shipped_at).toLocaleString() : '-'}
                      </td>
                      <td>
                        {order.tracking_number && (
                          <div className="flex gap-1">
                            <a href={shippingLabelPdfUrl(order.tracking_number)} target="_blank" rel="noopener noreferrer" data-testid={`pdf-label-${order.tracking_number}`}>
                              <Button size="sm" variant="outline" className="border-brand text-brand text-xs">PDF</Button>
                            </a>
                            <a href={shippingLabelZplUrl(order.tracking_number)} download={`${order.tracking_number}.zpl`} data-testid={`zpl-label-${order.tracking_number}`}>
                              <Button size="sm" variant="outline" className="text-xs">ZPL</Button>
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
